/**

 * HUB-B.3 — Onglet Ventes (liste partagée + modales + accès Historique renforcé).

 */

import React, { useCallback } from 'react';

import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { canViewActiviteFinancialTotals } from '../../../utils/roleRouting';

import useActiviteVentes from '../../../hooks/activite/useActiviteVentes';

import useHistoriqueVenteActions from '../../../hooks/historique/useHistoriqueVenteActions';
import { printTicket80mm } from '../../../utils/ticket80mmPrint';

import ActiviteVentesKpiStrip from '../ActiviteVentesKpiStrip';

import ActiviteHistoriqueAccessBanner from '../ActiviteHistoriqueAccessBanner';

import HistoriqueVentesList from '../../historique/HistoriqueVentesList';

import HistoriqueVentesActionsBar from '../../historique/HistoriqueVentesActionsBar';

import HistoriqueVenteModals from '../../historique/HistoriqueVenteModals';

import HistoriqueSumUpPendingPanel from '../../historique/HistoriqueSumUpPendingPanel';

import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';



export default function ActiviteVentesTab({ filters }) {

  const { api, isAdmin, user } = useAuth();
  const isCarlosOnly = canViewActiviteFinancialTotals(user);

  const { tError } = useLanguage();

  const {

    ventes,

    loading,

    error,

    currentPage,

    totalPages,

    totaux,

    commissions,

    pendingPayments,

    fetchPendingPayments,

    setCurrentPage,

    refresh,

  } = useActiviteVentes(api, filters, { canViewFinancialTotals: isCarlosOnly });



  const onRefresh = useCallback(() => {

    refresh();

    fetchPendingPayments();

  }, [refresh, fetchPendingPayments]);



  const { convertingRef, actionHandlers, modals, testIdPrefix } = useHistoriqueVenteActions({

    api,

    onRefresh,

    testIdPrefix: 'activite',

  });



  const openPdfForPrint = async (endpoint) => {

    try {

      const response = await api.get(endpoint, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank', 'width=900,height=700');

      if (!printWindow) {

        alert('Veuillez autoriser les popups pour imprimer.');

        return;

      }

      const triggerPrint = () => {

        printWindow.focus();

        printWindow.print();

      };

      printWindow.addEventListener('load', triggerPrint);

      setTimeout(triggerPrint, 1200);

      setTimeout(() => window.URL.revokeObjectURL(url), 8000);

    } catch (err) {

      alert(tError(err.response?.data?.detail) || 'Erreur lors du téléchargement');

    }

  };



  const handleRapportVendeur = async () => {

    if (!filters.vendeur || !filters.date_debut || !filters.date_fin) {

      alert('Veuillez renseigner vendeur + dates dans les filtres');

      return;

    }

    try {
      await printTicket80mm(api, {
        type: 'rapport_vendeur',
        vendeur: filters.vendeur,
        dateDebut: filters.date_debut,
        dateFin: filters.date_fin,
      });
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur impression rapport');
    }
  };



  const handleConfirmSumupPayment = async (checkoutId) => {
    try {
      await api.post(`/payments/sumup/staff-confirm/${checkoutId}`);
      onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur lors de la confirmation SumUp');
    }
  };

  const handleCancelSumupPayment = async (checkoutId) => {
    if (!window.confirm('Marquer ce paiement comme refusé / annulé ?')) return;
    try {
      await api.post(`/payments/sumup/cancel/${checkoutId}`);
      onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur lors du refus SumUp');
    }
  };



  return (

    <div

      className="rounded-xl border p-4 sm:p-5 space-y-4"

      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}

      data-testid="activite-ventes-tab"

    >

      <ActiviteHistoriqueAccessBanner filters={filters} />



      <HistoriqueSumUpPendingPanel
        payments={pendingPayments}
        onConfirm={handleConfirmSumupPayment}
        onCancel={handleCancelSumupPayment}
        testIdPrefix="activite"
      />



      <ActiviteVentesKpiStrip totaux={totaux} commissions={commissions} isCarlosOnly={isCarlosOnly} />



      <HistoriqueVentesActionsBar

        testIdPrefix="activite"

        onRetrait={() => modals.setShowRetraitModal(true)}

        onRapportVendeur={handleRapportVendeur}

      />



      {error && (

        <div

          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"

          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}

          data-testid="activite-ventes-error"

        >

          {error}

        </div>

      )}



      <HistoriqueVentesList

        ventes={ventes}

        loading={loading}

        isAdmin={isAdmin}

        currentPage={currentPage}

        totalPages={totalPages}

        onPageChange={setCurrentPage}

        convertingRef={convertingRef}

        actionHandlers={actionHandlers}

        testIdPrefix={testIdPrefix}

        emptyDesktopLabel="Aucune vente pour cette période"

        emptyMobileLabel="Aucune vente pour cette période"

      />



      <HistoriqueVenteModals api={api} modals={modals} />



      <span className="sr-only" data-testid="activite-ventes-tab-id">{ACTIVITE_TAB_IDS.VENTES}</span>

    </div>

  );

}

